import React from 'react';
import { observer } from 'mobx-react';
import { Button, CircularProgress } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { FormControl, TextField } from '@mui/material';
import clazzes from 'classnames';
import { useTranslation } from 'react-i18next';
import { TextDefault } from '../../design-system/texts';
import './password.scss';
import { QuestionHeading } from '../question-heading';
import PresentationHeading from './presentation-heading';
import { Heading3 } from '../../design-system/headings';
import { ThemedButton } from '../../themed/themed-button/ThemedButton';

export const Password = observer(({ ui, onAction, scorePercentage }) => {
    const useStyles = makeStyles((theme) => ({
        root: {
            display: 'flex',
            alignItems: 'center',
        },
        wrapper: {
            margin: theme.spacing(1),
            position: 'relative',
        },
        buttonProgress: {
            color: '#ffffff',
            position: 'absolute',
            top: '30%',
            right: '40%',
            zIndex: 1000,
            '@media (max-width: 992px)': {
                top: 'initial',
                right: 'initial',
                position: 'initial',
            },
        },
        wrap: {
            width: '100%',
            padding: '20px',
            background: '#FFFFFF',
            border: '1px solid rgba(232, 234, 233, 0.5)',
            boxSizing: 'border-box',
            boxShadow:
                '0px 0px 2px rgba(0, 0, 0, 0.08), 0px 4px 24px rgba(29, 30, 29, 0.08)',
            borderRadius: 8,
            marginTop: '24px',
        },

        description: {
            fontSize: '12px',
        },
    }));

    const { t } = useTranslation('assessment');
    const classes = useStyles();
    //const {brandingStore} = useStores();
    const [loading, setLoading] = React.useState(false);
    const [showingResults, setShowingResults] = React.useState(false);
    const [missingFromPassword, setMissingFromPassword] = React.useState([]);
    const [passwordResolution, setPasswordResolution] = React.useState({});
    const [passwordVisible, setPasswordVisibility] = React.useState(true);

    const redColor = '#EB5757';
    const yellowColor = '#FCBE00';
    const greenColor = '#20A26B';

    const passwordShouldContain = [
        'Lowercase',
        'Uppercase',
        'Number',
        'Symbols',
        'Length',
    ];

    const passwordInput = React.createRef();
    const timer = React.useRef();

    const passwordForm = ui['password-form'];

    const uppercaseRegExp = /(?=.*[\p{Lu}])/u;
    const lowercaseRegExp = /(?=.*[\p{Ll}])/u;
    const numberRegExp = /(?=.*\d)/;
    const specialRegExp = /(?=.*[\p{P}\p{S}])/u;

    React.useEffect(() => {
        return () => {
            clearTimeout(timer.current);
        };
    }, []);

    const togglePasswordVisibility = () => {
        setPasswordVisibility(!passwordVisible);
    };

    const checkPassword = (event) => {
        const password = passwordInput.current.value;
        //Set spinner to button for few seconds
        if (!loading) {
            setLoading(true);
            timer.current = window.setTimeout(() => {
                setLoading(false);
                setShowingResults(true);
            }, 2000);
        }

        //handleChange that user did input password
        if (password.length > 0) {
            onAction && onAction(passwordForm.clickActionId);
        }

        //Check password for
        let missing = [];
        if (!uppercaseRegExp.test(password)) {
            missing.push('Uppercase');
        }
        if (!lowercaseRegExp.test(password)) {
            missing.push('Lowercase');
        }
        if (!numberRegExp.test(password)) {
            missing.push('Number');
        }
        if (!specialRegExp.test(password)) {
            missing.push('Symbols');
        }
        if (password.length < 12) {
            missing.push('Length');
        }

        setPasswordResolution(resolvePassword(missing.length));
        setMissingFromPassword(missing);
    };

    const resolvePassword = (missingCount) => {
        switch (missingCount) {
            case 0:
                return { text: t('Very strong'), percentage: 100 };
            case 1:
                return { text: t('Strong'), percentage: 75 };
            case 2:
                return { text: t('Medium'), percentage: 50 };
            case 3:
                return { text: t('Weak'), percentage: 25 };
            default:
                return { text: t('Very weak'), percentage: 0 };
        }
    };

    const calculateColor = (percentage) => {
        if (percentage <= 33) return redColor;
        if (percentage <= 67) return yellowColor;
        return greenColor;
    };

    return (
        <div className="assessment-presentation">
            <div className="question">
                <PresentationHeading ui={ui} />
                <div className={classes.wrap}>
                    <Heading3>{t(ui.componentHeader)} </Heading3>
                    <p className={classes.description}>
                        {t(ui.componentDescription)}
                    </p>

                    <div>
                        <Heading3>
                            {t('Not sure? Check your password here')}
                        </Heading3>
                        <p style={{ fontSize: '14px' }}>
                            {t(
                                'The stronger your password is, the harder it is to hack. Test your password strength here.'
                            )}
                        </p>
                        <div
                            className={clazzes('password-tester', {
                                visible: passwordVisible,
                            })}
                        >
                            <FormControl>
                                <TextField
                                    style={{ background: '#FCFCFC' }}
                                    inputRef={passwordInput}
                                    id="password-tester-password"
                                    variant="outlined"
                                    placeholder={t('Type in your password')}
                                    type={passwordVisible ? 'password' : 'text'}
                                    className={clazzes('password-input')}
                                />
                                <div
                                    className="show-password"
                                    onClick={() => togglePasswordVisibility()}
                                />
                                <Button
                                    variant="contained"
                                    color="primary"
                                    component="span"
                                    type="submit"
                                    onClick={checkPassword}
                                    className={clazzes({
                                        'button-disabled': loading,
                                    })}
                                >
                                    {loading ? (
                                        <CircularProgress
                                            size={24}
                                            className={classes.buttonProgress}
                                        />
                                    ) : (
                                        t('Test')
                                    )}
                                </Button>
                            </FormControl>
                        </div>
                    </div>
                    {showingResults && (
                        <div className="password-tester-results">
                            <TextDefault bold>
                                {t('Your password is')}:
                            </TextDefault>{' '}
                            <TextDefault extraBold>
                                {passwordResolution.text}{' '}
                            </TextDefault>
                            <div className="meter">
                                <span
                                    style={{
                                        width:
                                            passwordResolution.percentage + '%',
                                        backgroundColor: calculateColor(
                                            passwordResolution.percentage
                                        ),
                                    }}
                                ></span>
                            </div>
                            <ul>
                                {passwordShouldContain.map((criteria, i) => {
                                    return (
                                        <li
                                            className={clazzes(
                                                'action',
                                                {
                                                    incorrect:
                                                        missingFromPassword.indexOf(
                                                            criteria
                                                        ) >= 0,
                                                },
                                                {
                                                    correct:
                                                        missingFromPassword.indexOf(
                                                            criteria
                                                        ) < 0,
                                                }
                                            )}
                                            key={'criteria-' + i}
                                        >
                                            {t(criteria)}
                                        </li>
                                    );
                                })}
                            </ul>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
});
